import React from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBolt,
  FaArrowRight,
  FaPlayCircle,
  FaCertificate,
  FaChartLine,
  FaYoutube,
  FaCheckCircle,
  FaWaveSquare,
  FaMicrochip,
} from "react-icons/fa";

import "./tailwindstyles.css";

const TrainingPage = () => {
  const navigate = useNavigate();

  const courses = [
    {
      title: "Energy Management",
      desc:
        "Master intelligent energy monitoring, optimization, analytics and real-time industrial power systems.",
      route: "/training/energy-management",
      color:
        "from-orange-500/[0.35] via-yellow-400/[0.25] to-orange-300/[0.18]",
      btn:
        "from-orange-500 via-yellow-400 to-orange-500",
      icon: <FaBolt />,
    },

    {
      title: "Power Systems",
      desc:
        "Understand generation, protection, transmission and modern smart grid technologies.",
      route: "/training/power-systems",
      color:
        "from-sky-500/[0.35] via-cyan-400/[0.25] to-sky-300/[0.18]",
      btn:
        "from-sky-500 via-cyan-400 to-sky-500",
      icon: <FaChartLine />,
    },

    {
      title: "DC Machines",
      desc:
        "Learn motors, generators, efficiency, industrial controls and real-world electrical applications.",
      route: "/training/dc-machines",
      color:
        "from-green-500/[0.35] via-emerald-400/[0.25] to-green-300/[0.18]",
      btn:
        "from-green-500 via-emerald-400 to-green-500",
      icon: <FaPlayCircle />,
    },

    {
      title: "DC Circuits",
      desc:
        "Learn circuit analysis, voltage laws, current flow, network theorems and practical electrical fundamentals.",
      route: "/training/dc-circuits",
      color:
        "from-pink-500/[0.35] via-rose-400/[0.25] to-pink-300/[0.18]",
      btn:
        "from-pink-500 via-rose-400 to-pink-500",
      icon: <FaMicrochip />,
    },

    {
      title: "AC Circuits",
      desc:
        "Understand phasors, impedance, resonance, power factor and alternating current circuit analysis.",
      route: "/training/ac-circuits",
      color:
        "from-violet-500/[0.35] via-purple-400/[0.25] to-violet-300/[0.18]",
      btn:
        "from-violet-500 via-purple-400 to-violet-500",
      icon: <FaWaveSquare />,
    },

    {
      title: "AC Machines",
      desc:
        "Master transformers, induction motors, synchronous machines and industrial AC applications.",
      route: "/training/ac-machines",
      color:
        "from-cyan-500/[0.35] via-blue-400/[0.25] to-cyan-300/[0.18]",
      btn:
        "from-cyan-500 via-blue-400 to-cyan-500",
      icon: <FaCertificate />,
    },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden text-white">

      {/* ================= BACKGROUND ================= */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-[#0b0f19] to-[#111827] opacity-95"></div>

      {/* ================= PREMIUM GLOW ================= */}
      <div className="absolute inset-0 opacity-40 pointer-events-none">

        <div
          className="
            absolute top-0 left-0 w-[120%] h-[120%]
            bg-gradient-to-r from-orange-500/30 via-yellow-400/20 to-sky-500/30
            blur-3xl animate-slowWave
          "
        ></div>

        <div
          className="
            absolute bottom-0 right-0 w-[120%] h-[120%]
            bg-gradient-to-r from-sky-500/20 via-orange-300/20 to-yellow-400/30
            blur-3xl animate-fastWave
          "
        ></div>

      </div>

      {/* ================= GRID ================= */}
      <div
        className="
          absolute inset-0 pointer-events-none
          bg-[linear-gradient(to_right,rgba(255,255,255,0.04)_1px,transparent_1px),
               linear-gradient(to_bottom,rgba(255,255,255,0.04)_1px,transparent_1px)]
          bg-[size:60px_60px]
        "
      />

      {/* ================= FLOATING PARTICLES ================= */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">

        {[...Array(22)].map((_, i) => (
          <span
            key={i}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${10 + Math.random() * 10}s`,
              width: `${4 + Math.random() * 5}px`,
              height: `${4 + Math.random() * 5}px`,
            }}
          />
        ))}

      </div>

      {/* ================= HERO ================= */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-24 pb-20">

        <div className="text-center max-w-5xl mx-auto">

          {/* Badge */}
          <div
            className="
              inline-flex items-center gap-3
              px-6 py-3 rounded-full
              border border-white/10
              bg-white/[0.05]
              backdrop-blur-xl
              shadow-[0_10px_40px_rgba(0,0,0,0.35)]
              mb-8
              animate-fadeUp
            "
          >

            <div className="w-2 h-2 rounded-full bg-orange-400 animate-pulse"></div>

            <p className="uppercase tracking-[4px] text-orange-300 text-[11px] font-medium">
              Power Sense Online Courses
            </p>

          </div>

          {/* Heading */}
          <h1
            className="
              text-5xl md:text-7xl
              font-black
              leading-[0.92]
              tracking-tight
              animate-fadeUp
            "
          >

            Advance Your Skills With

            <span
              className="
                block mt-3
                bg-gradient-to-r
                from-orange-300
                via-yellow-200
                to-cyan-300
                bg-clip-text text-transparent
                animate-gradientMove
                bg-[length:200%_200%]
              "
            >
              Online Courses
            </span>

          </h1>

          {/* Description */}
          <p
            className="
              mt-8 max-w-3xl mx-auto
              text-slate-300
              text-base md:text-xl
              leading-relaxed
              animate-fadeUpDelay
            "
          >
            Learn directly from industry experts and gain practical
            knowledge in power systems, smart technologies,
            electrical engineering and industrial applications
            through structured online learning.
          </p>

          {/* ================= BUTTONS ================= */}
          <div className="mt-10 flex flex-wrap justify-center gap-5">

            {/* Explore Courses Button */}
            <button
              onClick={() => {
                const section = document.getElementById("courses");

                if (section) {
                  const yOffset = -100;

                  const y =
                    section.getBoundingClientRect().top +
                    window.pageYOffset +
                    yOffset;

                  window.scrollTo({
                    top: y,
                    behavior: "smooth",
                  });
                }
              }}
              className="
                group
                px-8 py-4 rounded-2xl
                bg-gradient-to-r from-orange-500 via-yellow-400 to-orange-500
                text-black font-bold
                hover:scale-105
                transition-all duration-500
                shadow-[0_10px_40px_rgba(255,196,0,0.35)]
              "
            >

              <span className="flex items-center">

                Explore Courses

                <FaArrowRight
                  className="
                    ml-3
                    transition-transform duration-500
                    group-hover:translate-x-1
                  "
                />

              </span>

            </button>

            {/* Watch Videos Button */}
            <a
              href="https://www.youtube.com/@pradeepyemula6015"
              target="_blank"
              rel="noopener noreferrer"
              className="
                group
                px-8 py-4 rounded-2xl
                border border-white/10
                bg-white/[0.05]
                backdrop-blur-xl
                hover:bg-white/[0.08]
                transition-all duration-500
              "
            >

              <span className="flex items-center">

                <FaYoutube className="mr-3 text-red-400 text-xl" />

                Watch Training Videos

              </span>

            </a>

          </div>

          {/* Profile */}
          <div
            className="
              mt-16
              max-w-3xl mx-auto
              rounded-[2rem]
              border border-white/10
              bg-gradient-to-br
              from-orange-400/[0.20]
              via-yellow-300/[0.14]
              to-cyan-400/[0.10]
              backdrop-blur-2xl
              p-8
              shadow-[0_10px_50px_rgba(0,0,0,0.35)]
            "
          >

            <div
              className="
                absolute inset-0 opacity-50
                bg-gradient-to-br
                from-orange-300/[0.15]
                via-yellow-200/[0.10]
                to-sky-300/[0.10]
                blur-3xl
              "
            ></div>

            <div className="relative z-10">

              <h2 className="text-3xl md:text-4xl font-black text-white">
                Prof. Pradeep Yamula
              </h2>

              <p className="mt-3 text-white/80 text-lg">
                CEO of Power Sense
              </p>

              <p className="mt-5 text-white/90 leading-relaxed text-base md:text-lg">
                Industry-focused online learning programs designed
                to help students and professionals build strong
                technical foundations and practical engineering skills.
              </p>

            </div>

          </div>

        </div>

      </section>

      {/* ================= COURSES ================= */}
      <section
        id="courses"
        className="relative z-10 max-w-7xl mx-auto px-6 pb-24"
      >

        {/* Heading */}
        <div className="text-center mb-14">

          <h2
            className="
              text-4xl md:text-5xl
              font-black
              text-white
              tracking-tight
            "
          >
            Professional Learning Tracks
          </h2>

          <p
            className="
              mt-5
              text-slate-300
              max-w-3xl
              mx-auto
              text-sm md:text-lg
              leading-relaxed
            "
          >
            Specialized programs designed for engineering students,
            professionals and future innovators.
          </p>

        </div>

        {/* Grid */}
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">

          {courses.map((course, idx) => (
            <div
              key={idx}
              className="
                group relative overflow-hidden
                rounded-[2rem]
                h-[430px]
                border border-white/10
                bg-gradient-to-br
                from-orange-400/[0.14]
                via-yellow-300/[0.10]
                to-cyan-400/[0.08]
                backdrop-blur-2xl
                p-6
                hover:-translate-y-3
                hover:border-white/20
                transition-all duration-700
                shadow-[0_15px_60px_rgba(0,0,0,0.35)]
              "
            >

              {/* Premium Glow */}
              <div
                className={`
                  absolute inset-0 opacity-60
                  bg-gradient-to-br ${course.color}
                  blur-3xl
                `}
              ></div>

              {/* Hover Glow */}
              <div
                className="
                  absolute inset-0 opacity-0
                  group-hover:opacity-70
                  bg-white/10
                  blur-3xl
                  transition-all duration-700
                "
              ></div>

              {/* Top Border */}
              <div
                className="
                  absolute top-0 left-0
                  w-full h-[2px]
                  bg-gradient-to-r
                  from-transparent
                  via-white/40
                  to-transparent
                "
              ></div>

              {/* Content */}
              <div className="relative z-10 flex flex-col h-full">

                {/* Icon */}
                <div
                  className="
                    w-14 h-14
                    rounded-2xl
                    bg-white/[0.10]
                    border border-white/10
                    flex items-center justify-center
                    text-xl text-white
                    mb-6
                    group-hover:scale-110
                    group-hover:rotate-6
                    transition-all duration-500
                  "
                >

                  {course.icon}

                </div>

                {/* Title */}
                <h3
                  className="
                    text-2xl
                    font-black
                    text-white
                    leading-tight
                  "
                >
                  {course.title}
                </h3>

                {/* Description */}
                <p
                  className="
                    mt-4
                    text-white/85
                    leading-relaxed
                    text-sm
                  "
                >
                  {course.desc}
                </p>

                {/* Features */}
                <div className="mt-6 space-y-3">

                  {[
                    "HD Video Tutorials",
                    "Take-Home Exam",
                    "Certification by Power Sense Pvt. Ltd.",
                    "Industry-Focused Learning",
                  ].map((feature, i) => (
                    <div
                      key={i}
                      className="flex items-center text-white/90"
                    >

                      <FaCheckCircle
                        className="
                          text-yellow-300
                          mr-3
                          text-xs
                          flex-shrink-0
                        "
                      />

                      <span className="text-sm leading-relaxed">
                        {feature}
                      </span>

                    </div>
                  ))}

                </div>

                {/* Button */}
                <div className="mt-auto pt-7">

                  <button
                    onClick={() => navigate(course.route)}
                    className={`
                      group inline-flex items-center
                      px-6 py-3 rounded-2xl
                      bg-gradient-to-r ${course.btn}
                      text-black font-bold text-sm
                      hover:scale-105
                      transition-all duration-500
                      shadow-[0_10px_35px_rgba(255,200,80,0.30)]
                    `}
                  >

                    Enroll Now

                    <FaArrowRight
                      className="
                        ml-3
                        transition-transform duration-500
                        group-hover:translate-x-1
                      "
                    />

                  </button>

                </div>

              </div>

            </div>
          ))}

        </div>

      </section>

      {/* ================= ANIMATIONS ================= */}
      <style>{`

        @keyframes slowWave {
          0% {
            transform: translate(-10%, -10%) rotate(0deg);
          }

          50% {
            transform: translate(10%, 10%) rotate(20deg);
          }

          100% {
            transform: translate(-10%, -10%) rotate(0deg);
          }
        }

        @keyframes fastWave {
          0% {
            transform: translate(10%, 10%) rotate(0deg);
          }

          50% {
            transform: translate(-10%, -10%) rotate(-20deg);
          }

          100% {
            transform: translate(10%, 10%) rotate(0deg);
          }
        }

        @keyframes gradientMove {
          0% {
            background-position: 0% 50%;
          }

          50% {
            background-position: 100% 50%;
          }

          100% {
            background-position: 0% 50%;
          }
        }

        @keyframes particleFloat {

          0% {
            transform: translateY(100vh) scale(0);
            opacity: 0;
          }

          10% {
            opacity: 0.4;
          }

          50% {
            opacity: 0.8;
          }

          100% {
            transform: translateY(-120vh) scale(1);
            opacity: 0;
          }
        }

        @keyframes fadeUp {

          0% {
            opacity: 0;
            transform: translateY(40px);
          }

          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-slowWave {
          animation: slowWave 14s ease-in-out infinite;
        }

        .animate-fastWave {
          animation: fastWave 10s ease-in-out infinite;
        }

        .animate-gradientMove {
          animation: gradientMove 6s ease infinite;
        }

        .animate-fadeUp {
          animation: fadeUp 1s ease forwards;
        }

        .animate-fadeUpDelay {
          animation: fadeUp 1.3s ease forwards;
        }

        .particle {
          position: absolute;
          bottom: -20px;
          border-radius: 999px;

          background:
            radial-gradient(circle,
            rgba(255,255,255,0.9) 0%,
            rgba(249,115,22,0.6) 40%,
            transparent 80%);

          filter: blur(1px);

          animation-name: particleFloat;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
        }

      `}</style>

    </div>
  );
};

export default TrainingPage;